import { useMemo, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { MediaVideoCard } from '@/components/media/MediaVideoCard'
import { MediaCardSkeleton } from '@/components/media/MediaCardSkeleton'
import type { MediaVideo } from '@/types/media'
import {
  SLIDER_EDGE_INNER,
  SLIDER_NAV_ICON_STROKE,
  sliderNavIconClass,
  sliderNavNextClass,
  sliderNavPrevClass,
} from '@/lib/sliderNavStyles'
import { cn } from '@/lib/utils'

interface MediaSliderProps {
  videos: MediaVideo[]
  isLoading?: boolean
  onPlay: (video: MediaVideo) => void
}

export function MediaSlider({ videos, isLoading, onPlay }: MediaSliderProps) {
  const [swiper, setSwiper] = useState<SwiperType | null>(null)
  const canLoop = videos.length > 4
  const navIcon = sliderNavIconClass('md')

  const slides = useMemo(() => videos, [videos])

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <MediaCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  if (!slides.length) return null

  return (
    <div className="relative overflow-hidden md:overflow-visible md:pr-[22px]">
      <div className={SLIDER_EDGE_INNER}>
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          loop={canLoop}
          spaceBetween={16}
          slidesPerView={1}
          slidesPerGroup={1}
          watchOverflow
          observer
          observeParents
          autoplay={
            slides.length > 1
              ? { delay: 4500, disableOnInteraction: false, pauseOnMouseEnter: true }
              : false
          }
          breakpoints={{
            640: { slidesPerView: 1, spaceBetween: 16 },
            768: { slidesPerView: 2, spaceBetween: 18 },
            1280: { slidesPerView: 4, spaceBetween: 20 },
          }}
          pagination={{ clickable: true }}
          onSwiper={setSwiper}
          className="media-insights-swiper overflow-hidden pb-10"
        >
          {slides.map((video) => (
            <SwiperSlide key={video.id} className="h-auto">
              <MediaVideoCard video={video} onPlay={onPlay} />
            </SwiperSlide>
          ))}
        </Swiper>

        {slides.length > 1 ? (
          <>
            <button
              type="button"
              aria-label="Previous videos"
              className={cn(sliderNavPrevClass('md'), 'media-slider-prev')}
              onClick={() => swiper?.slidePrev()}
            >
              <ChevronLeft className={navIcon} strokeWidth={SLIDER_NAV_ICON_STROKE} />
            </button>
            <button
              type="button"
              aria-label="Next videos"
              className={cn(sliderNavNextClass('md'), 'media-slider-next')}
              onClick={() => swiper?.slideNext()}
            >
              <ChevronRight className={navIcon} strokeWidth={SLIDER_NAV_ICON_STROKE} />
            </button>
          </>
        ) : null}
      </div>
    </div>
  )
}
